import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import api from "../api/client";
import Navbar from "../components/layout/Navbar";
import MobileNavbar from "../components/layout/MobileNavbar";
import SearchBar from "../components/dashboard/SearchBar";
import DateFilter from "../components/dashboard/DateFilter";
import XlsxImportDetailModal from "../components/modals/XlsxImportDetailModal";
import XlsxImportMapModal from "../components/modals/XlsxImportMapModal";
import CsvImportModal from "../components/modals/CsvImportModal";
import TurnInsView from "../components/xlsx-imports/TurnInsView";
import SearchableTLSelect from "../components/common/SearchableTLSelect";
import { getCompanyBadgeClass } from "../utils/companyColors";
import useAuth from "../hooks/useAuth";
import useIsMobile from "../hooks/useIsMobile";

const STATUS_BADGE = {
  "Pending Review": "bg-yellow-100 text-yellow-800",
  Cancelled: "bg-red-100 text-red-700",
  Activated: "bg-green-100 text-green-700",
};

function StatusBadge({ status }) {
  const cls = STATUS_BADGE[status] || "bg-gray-100 text-gray-700";
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}

function formatDateTime(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleString();
}

function ConfirmDialog({ title, message, onConfirm, onCancel, confirmLabel = "Confirm", danger = true }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
        <h3 className="text-base font-semibold text-gray-800 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-4">{message}</p>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm border rounded hover:bg-gray-100"
          >
            Go Back
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm rounded text-white ${danger ? "bg-red-600 hover:bg-red-700" : "bg-orange-500 hover:bg-orange-600"}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function InlineTLSelect({ record, activeTLs, onSave }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const filtered = activeTLs.filter((tl) =>
    tl.name.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handler = (e) => {
      if (
        ref.current && !ref.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const handleSelect = async (tl) => {
    setOpen(false);
    setSearch("");
    setSaving(true);
    try {
      const { data } = await api.put(`/xlsx-imports/${record.id}`, {
        assigned_tl_id: tl.id,
        assigned_tl_name: tl.name,
      });
      onSave(data);
    } catch {
      // silently fail — user can retry
    } finally {
      setSaving(false);
    }
  };

  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  useEffect(() => {
    if (open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => { setOpen((v) => !v); setSearch(""); }}
        disabled={saving || record.status === "Cancelled"}
        className={`text-sm px-2 py-0.5 rounded cursor-pointer transition-colors ${
          record.assigned_tl_name
            ? "bg-purple-100 text-purple-800 hover:bg-purple-200"
            : "text-gray-400 italic hover:bg-gray-100"
        } ${saving ? "opacity-50" : ""}`}
        title="Click to assign TL"
      >
        {saving ? "..." : record.assigned_tl_name || "-"}
      </button>

      {open && createPortal(
        <div
          ref={dropdownRef}
          style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 9999 }}
          className="w-56 bg-white border rounded-lg shadow-lg max-h-60 flex flex-col"
        >
          <div className="p-2 border-b">
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search TL..."
              className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-400">No results</div>
            ) : (
              filtered.map((tl) => (
                <button
                  key={tl.id}
                  type="button"
                  onClick={() => handleSelect(tl)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${
                    tl.id === record.assigned_tl_id ? "bg-blue-600 text-white hover:bg-blue-700" : "text-gray-800"
                  }`}
                >
                  {tl.name}
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function InlineCompanySelect({ record, companies, canEdit, onSave }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);
  const dropdownRef = useRef(null);
  const btnRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const handler = (e) => {
      if (
        ref.current && !ref.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
  }, [open]);

  const handleSelect = async (companyId) => {
    setOpen(false);
    setSaving(true);
    try {
      const { data } = await api.put(`/xlsx-imports/${record.id}/company`, {
        company_id: companyId,
      });
      onSave(data);
    } catch {
      // silently fail — user can retry
    } finally {
      setSaving(false);
    }
  };

  const companyLabel = record.company_prefix || record.company_name;

  // View-only rendering when the user lacks the edit permission
  if (!canEdit) {
    return companyLabel
      ? <span className={`text-sm px-2 py-0.5 rounded ${getCompanyBadgeClass(companyLabel)}`} title={record.company_name || ""}>{companyLabel}</span>
      : <span className="text-gray-400 italic">-</span>;
  }

  return (
    <div ref={ref} className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={saving || record.status === "Cancelled"}
        className={`text-sm px-2 py-0.5 rounded cursor-pointer transition-colors ${
          companyLabel
            ? getCompanyBadgeClass(companyLabel)
            : "text-gray-400 italic hover:bg-gray-100"
        } ${saving ? "opacity-50" : ""}`}
        title={record.company_name || "Click to assign Company"}
      >
        {saving ? "..." : companyLabel || "Select…"}
      </button>

      {open && createPortal(
        <div
          ref={dropdownRef}
          style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 9999 }}
          className="w-56 bg-white border rounded-lg shadow-lg max-h-60 flex flex-col"
        >
          <div className="overflow-y-auto flex-1">
            {record.company_id && (
              <button
                type="button"
                onClick={() => handleSelect(null)}
                className="w-full text-left px-3 py-2 text-sm text-gray-500 italic hover:bg-gray-50 border-b"
              >
                Clear
              </button>
            )}
            {companies.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-400">No active companies</div>
            ) : (
              companies.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => handleSelect(c.id)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${
                    c.id === record.company_id ? "bg-blue-600 text-white hover:bg-blue-700" : "text-gray-800"
                  }`}
                  title={c.name}
                >
                  {c.prefix || c.name}
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default function XlsxImportsPage() {
  const { canImportXlsx, canActivateStaging, canDeleteXlsx, hasPerm } = useAuth();
  const canEditCompany = hasPerm("field.edit.company");
  const isMobile = useIsMobile();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detailPatch, setDetailPatch] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showImportActive, setShowImportActive] = useState(false);
  const [search, setSearch] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [pendingAction, setPendingAction] = useState(null);
  const [actionError, setActionError] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [activeTLs, setActiveTLs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [showBulkActivate, setShowBulkActivate] = useState(false);
  const [bulkActivateTLId, setBulkActivateTLId] = useState(null);
  const [mapRecord, setMapRecord] = useState(null); // record to open in XlsxImportMapModal

  // Date filter state — default to today
  const getToday = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const [dateRange, setDateRange] = useState({ from: getToday(), to: getToday() });

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/xlsx-imports");
      setRecords(data);
    } catch {
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  useEffect(() => {
    api.get("/team-leaders/active").then(({ data }) => setActiveTLs(data)).catch(() => {});
    api.get("/companies/active").then(({ data }) => setCompanies(data)).catch(() => {});
  }, []);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortedRecords = useMemo(() => {
    if (!sortKey) return records; // backend order (newest-first) by default
    return [...records].sort((a, b) => {
      let av = a[sortKey] ?? "";
      let bv = b[sortKey] ?? "";
      if (typeof av === "string") av = av.toLowerCase();
      if (typeof bv === "string") bv = bv.toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [records, sortKey, sortDir]);

  // Filter records by search
  const filteredAndSortedRecords = useMemo(() => {
    if (!search) return sortedRecords;
    const searchLower = search.toLowerCase();
    return sortedRecords.filter((r) => {
      return (
        (r.jo_number && r.jo_number.toLowerCase().includes(searchLower)) ||
        (r.reference_id && r.reference_id.toLowerCase().includes(searchLower)) ||
        (r.account_number && r.account_number.toLowerCase().includes(searchLower)) ||
        (r.customer_full_name && r.customer_full_name.toLowerCase().includes(searchLower)) ||
        (r.team_leader_name && r.team_leader_name.toLowerCase().includes(searchLower))
      );
    });
  }, [sortedRecords, search]);

  // Turn-ins count: independent server-side count of ALL records by uploaded date
  const [turnInsCount, setTurnInsCount] = useState(0);
  const [activeTab, setActiveTab] = useState("bo"); // 'bo' | 'turnins'

  const fetchTurnInsCount = useCallback(() => {
    const params = {};
    if (dateRange.from) params.date_from = dateRange.from;
    if (dateRange.to) params.date_to = dateRange.to;
    api.get("/xlsx-imports/turn-ins-count", { params })
      .then(({ data }) => setTurnInsCount(data.total))
      .catch(() => setTurnInsCount(0));
  }, [dateRange]);

  useEffect(() => {
    fetchTurnInsCount();
  }, [fetchTurnInsCount]);

  // Combined refresh for use after imports/deletes/activations
  const refreshAll = useCallback(async () => {
    await fetchRecords();
    fetchTurnInsCount();
  }, [fetchRecords, fetchTurnInsCount]);

  const pageRecords = filteredAndSortedRecords;

  const pageIds = pageRecords.map((r) => r.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const executeAction = async () => {
    const action = pendingAction;
    setPendingAction(null);
    if (!action) return;
    try {
      if (action.type === "cancel") {
        await api.post(`/xlsx-imports/${action.id}/cancel`);
      } else if (action.type === "delete") {
        await api.delete(`/xlsx-imports/${action.id}`);
        setSelected((prev) => { const n = new Set(prev); n.delete(action.id); return n; });
      } else if (action.type === "deleteSelected") {
        await Promise.all([...action.ids].map((id) => api.delete(`/xlsx-imports/${id}`)));
        setSelected(new Set());
      }
      await refreshAll();
    } catch {
      await refreshAll();
    }
  };

  const handleSearch = useCallback((value) => {
    setSearch(value);
    setSelected(new Set());
  }, []);

  return (
    <div className="h-screen flex flex-col bg-gray-100 overflow-hidden">
      {isMobile ? <MobileNavbar /> : <Navbar />}

      <main className="flex-1 flex flex-col min-h-0 px-4 lg:px-6 pt-3 pb-2 w-full">
        {/* Toolbar */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-3 flex-shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <SearchBar onSearch={handleSearch} />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <DateFilter
              value={dateRange}
              onChange={setDateRange}
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-end border-b border-gray-200 mb-3 flex-shrink-0">
          <button
            type="button"
            onClick={() => setActiveTab("bo")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === "bo"
                ? "border-blue-600 text-blue-700"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Back Office Imports
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("turnins")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === "turnins"
                ? "border-blue-600 text-blue-700"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Turn-ins: <span className="font-bold">{turnInsCount}</span>
          </button>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-3 flex-shrink-0">
          <h1 className="text-xl font-semibold text-gray-800">
            {activeTab === "bo" ? "Back Office Imports" : "Turn-ins"}
          </h1>
          {activeTab === "bo" && (
          <div className="flex flex-wrap items-center gap-2">
            {selected.size > 0 && (
              <>
                {canActivateStaging && (() => {
                  const incompleteSelected = Array.from(selected)
                    .map((id) => records.find((r) => r.id === id))
                    .filter((r) => r && (!r.complete_address || !r.location_saved || !r.company_id));
                  const canBulkActivate = incompleteSelected.length === 0;
                  return (
                    <button
                      onClick={() => {
                        if (!canBulkActivate) return;
                        setBulkActivateTLId(null);
                        setShowBulkActivate(true);
                      }}
                      disabled={!canBulkActivate}
                      title={
                        !canBulkActivate
                          ? `${incompleteSelected.length} selected record${incompleteSelected.length > 1 ? "s are" : " is"} missing address, pin location, or company`
                          : undefined
                      }
                      className={`text-white text-sm px-4 py-2 rounded transition-colors ${
                        canBulkActivate
                          ? "bg-green-600 hover:bg-green-700 cursor-pointer"
                          : "bg-green-300 cursor-not-allowed opacity-70"
                      }`}
                    >
                      Make Job Order Active ({selected.size})
                    </button>
                  );
                })()}
                {canDeleteXlsx && (
                  <button
                    onClick={() => setPendingAction({ type: "deleteSelected", ids: new Set(selected) })}
                    className="bg-red-600 text-white text-sm px-4 py-2 rounded hover:bg-red-700"
                  >
                    Delete Selected ({selected.size})
                  </button>
                )}
              </>
            )}
            {canImportXlsx && (
              <>
                <button
                  onClick={() => setShowImport(true)}
                  className="bg-blue-600 text-white text-sm px-4 py-2 rounded hover:bg-blue-700"
                >
                  Import FSOI
                </button>
                <button
                  onClick={() => setShowImportActive(true)}
                  className="bg-emerald-600 text-white text-sm px-4 py-2 rounded hover:bg-emerald-700"
                >
                  Import Active
                </button>
              </>
            )}
          </div>
          )}
        </div>

        {actionError && (
          <div className="bg-blue-50 border border-blue-200 text-blue-700 text-sm rounded px-4 py-3 mb-3 flex-shrink-0">
            {actionError}
          </div>
        )}

        {activeTab === "turnins" ? (
          <TurnInsView dateRange={dateRange} />
        ) : loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-500">Loading...</div>
        ) : (
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 flex flex-col min-h-0 border rounded-lg overflow-hidden">
              <div className="flex-1 min-h-0 overflow-x-auto" style={{ overflowY: "auto" }}>
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 border-b sticky top-0 z-10">
                    <tr>
                      <th className="px-2 py-3 w-12 text-center font-medium text-gray-600 whitespace-nowrap bg-gray-50">Count</th>
                      <th className="px-3 py-3 w-10 text-center bg-gray-50">
                        <button
                          onClick={toggleSelectPage}
                          title={allPageSelected ? "Deselect all" : "Select all rows"}
                          className={`w-5 h-5 border-2 rounded flex items-center justify-center mx-auto transition-colors text-xs ${
                            allPageSelected
                              ? "bg-blue-600 border-blue-600 text-white"
                              : "border-gray-400 hover:border-blue-500 text-transparent"
                          }`}
                        >
                          {allPageSelected ? "v" : ""}
                        </button>
                      </th>
                      {[
                        { label: "Turn-in Date", key: "job_date" },
                        { label: "Reference ID", key: "reference_id" },
                        { label: "JO Number", key: "jo_number" },
                        { label: "Account Number", key: "account_number" },
                        { label: "Customer Name", key: "customer_full_name" },
                        { label: "Address", key: "complete_address" },
                      ].map(({ label, key }) => (
                        <th
                          key={key}
                          onClick={() => handleSort(key)}
                          className="px-4 py-3 text-left font-medium text-gray-600 whitespace-nowrap cursor-pointer select-none hover:bg-gray-100 bg-gray-50"
                        >
                          <span className="inline-flex items-center gap-1">
                            {label}
                            {sortKey === key ? (
                              <span className="text-blue-600">{sortDir === "asc" ? "▲" : "▼"}</span>
                            ) : (
                              <span className="text-gray-300">⇅</span>
                            )}
                          </span>
                        </th>
                      ))}
                      <th className="px-4 py-3 text-center font-medium text-gray-600 whitespace-nowrap bg-gray-50">Pin</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600 whitespace-nowrap bg-gray-50">Company</th>
                      {[
                        { label: "Assigned TL", key: "assigned_tl_name" },
                        { label: "Sales Agent", key: "team_leader_name" },
                        { label: "Status", key: "status" },
                        { label: "Uploaded By", key: "imported_by_name" },
                      ].map(({ label, key }) => (
                        <th
                          key={key}
                          onClick={() => handleSort(key)}
                          className="px-4 py-3 text-left font-medium text-gray-600 whitespace-nowrap cursor-pointer select-none hover:bg-gray-100 bg-gray-50"
                        >
                          <span className="inline-flex items-center gap-1">
                            {label}
                            {sortKey === key ? (
                              <span className="text-blue-600">{sortDir === "asc" ? "▲" : "▼"}</span>
                            ) : (
                              <span className="text-gray-300">⇅</span>
                            )}
                          </span>
                        </th>
                      ))}
                      <th className="px-4 py-3 text-left font-medium text-gray-600 whitespace-nowrap bg-gray-50">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {pageRecords.length === 0 ? (
                      <tr>
                        <td colSpan={13} className="text-center py-10 text-gray-400">
                          No imported records yet.
                        </td>
                      </tr>
                    ) : (
                      pageRecords.map((r, idx) => (
                        <tr
                          key={r.id}
                          className={`hover:bg-blue-50 cursor-pointer transition-colors ${
                            selected.has(r.id) ? "bg-blue-50" : ""
                          }`}
                          onClick={() => {
                            setSelectedId(r.id);
                          }}
                        >
                          <td className="px-2 py-3 w-12 text-center text-gray-500 tabular-nums">
                            {idx + 1}
                          </td>
                          <td
                            className="px-3 py-3 w-10 text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleSelect(r.id);
                              }}
                              className={`w-5 h-5 border-2 rounded flex items-center justify-center mx-auto cursor-pointer transition-colors text-xs ${
                                selected.has(r.id)
                                  ? "bg-blue-600 border-blue-600 text-white"
                                  : "border-gray-400 hover:border-blue-500 text-transparent"
                              }`}
                            >
                              {selected.has(r.id) ? "v" : ""}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                            {r.job_date ? (() => {
                              const [y, m, d] = r.job_date.split("-");
                              return `${m}-${d}-${y}`;
                            })() : <span className="text-gray-400 italic">-</span>}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                            {r.reference_id || <span className="text-gray-400 italic">-</span>}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap font-mono text-gray-800">
                            {r.jo_number || <span className="text-gray-400 italic">-</span>}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                            {r.account_number || <span className="text-gray-400 italic">-</span>}
                          </td>
                          <td className="px-4 py-3 text-gray-800">{r.customer_full_name}</td>
                          {/* Address column */}
                          <td className="px-4 py-3 max-w-[200px]" title={r.complete_address || ""}>
                            {r.complete_address ? (
                              <span className="text-xs text-gray-700 line-clamp-2 leading-snug">
                                {r.complete_address}
                              </span>
                            ) : (
                              <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700">
                                No address
                              </span>
                            )}
                          </td>
                          {/* Pin column — always clickable */}
                          <td
                            className="px-4 py-3 text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              title={r.location_saved ? "Location saved — click to view/adjust" : "Location not yet set — click to set"}
                              onClick={() => setMapRecord(r)}
                              className={`inline-flex items-center justify-center w-7 h-7 rounded-full transition-colors ${
                                r.location_saved
                                  ? "text-green-600 hover:bg-green-50"
                                  : "text-red-500 hover:bg-red-50"
                              }`}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                              </svg>
                            </button>
                          </td>
                          {/* Company column */}
                          <td
                            className="px-4 py-3 whitespace-nowrap"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <InlineCompanySelect
                              record={r}
                              companies={companies}
                              canEdit={canEditCompany}
                              onSave={(updated) => {
                                setRecords((prev) => prev.map((rec) => rec.id === updated.id ? { ...rec, ...updated } : rec));
                              }}
                            />
                          </td>
                          <td
                            className="px-4 py-3 whitespace-nowrap"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <InlineTLSelect
                              record={r}
                              activeTLs={activeTLs}
                              onSave={(updated) => {
                                setRecords((prev) => prev.map((rec) => rec.id === updated.id ? { ...rec, ...updated } : rec));
                              }}
                            />
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {r.team_leader_name || <span className="text-gray-400 italic">-</span>}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <StatusBadge status={r.status} />
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                            {r.imported_by_name || <span className="text-gray-400 italic">-</span>}
                          </td>
                          <td
                            className="px-4 py-3 whitespace-nowrap"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex gap-1">
                              {/* Cancel button removed as requested */}
                              {canDeleteXlsx && (
                                <button
                                  onClick={() =>
                                    setPendingAction({
                                      type: "delete",
                                      id: r.id,
                                      name: r.customer_full_name,
                                    })
                                  }
                                  className="text-xs px-2 py-1 border border-red-300 text-red-700 rounded hover:bg-red-50"
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Record count footer */}
            <div className="flex-shrink-0 pt-1 text-sm text-gray-600">
              {filteredAndSortedRecords.length} record{filteredAndSortedRecords.length === 1 ? "" : "s"}
            </div>
          </div>
        )}
      </main>

      {selectedId && (
        <XlsxImportDetailModal
          recordId={selectedId}
          onClose={() => setSelectedId(null)}
          onRefresh={refreshAll}
          recordPatch={detailPatch}
        />
      )}

      {mapRecord && (
        <XlsxImportMapModal
          record={mapRecord}
          onClose={() => setMapRecord(null)}
          onSaved={(updated) => {
            setRecords((prev) =>
              prev.map((rec) => rec.id === updated.id ? { ...rec, ...updated } : rec)
            );
            if (selectedId === updated.id) {
              setDetailPatch({
                latitude: updated.latitude,
                longitude: updated.longitude,
                complete_address: updated.complete_address,
                location_saved: updated.location_saved,
              });
            }
            setMapRecord(null);
          }}
        />
      )}

      {showImport && (
        <CsvImportModal
          onClose={() => setShowImport(false)}
          onRefresh={refreshAll}
          importType="FSOI"
        />
      )}



      {showImportActive && (
        <CsvImportModal
          onClose={() => setShowImportActive(false)}
          onRefresh={refreshAll}
          importType="ACTIVE"
        />
      )}

      {pendingAction?.type === "cancel" && (
        <ConfirmDialog
          title="Cancel Staging Record"
          message={`Cancel the record for "${pendingAction.name}"? It will remain in the table but cannot be activated.`}
          confirmLabel="Yes, Cancel"
          danger={false}
          onConfirm={executeAction}
          onCancel={() => setPendingAction(null)}
        />
      )}

      {pendingAction?.type === "delete" && (
        <ConfirmDialog
          title="Delete Staging Record"
          message={`Permanently delete the record for "${pendingAction.name}"? This cannot be undone.`}
          confirmLabel="Yes, Delete"
          onConfirm={executeAction}
          onCancel={() => setPendingAction(null)}
        />
      )}

      {pendingAction?.type === "deleteSelected" && (
        <ConfirmDialog
          title="Delete Selected Records"
          message={`Permanently delete ${pendingAction.ids.size} selected record(s)? This cannot be undone.`}
          confirmLabel={`Delete ${pendingAction.ids.size} Record(s)`}
          onConfirm={executeAction}
          onCancel={() => setPendingAction(null)}
        />
      )}

      {showBulkActivate && (() => {
        // Check which selected records already have Assigned TL
        const selectedRecs = Array.from(selected).map((id) => records.find((r) => r.id === id)).filter(Boolean);
        const withoutTL = selectedRecs.filter((r) => !r.assigned_tl_id);
        const allHaveTL = withoutTL.length === 0;

        const doBulkActivate = async (teamLeaderId) => {
          setShowBulkActivate(false);
          let activatedCount = 0;
          let failedCount = 0;
          let failMessages = [];

          // Always update all selected records before activation
          const updatePromises = Array.from(selected).map(async (id) => {
            const rec = records.find((r) => r.id === id);
            if (rec) {
              const payload = {
                jo_number: rec.jo_number,
                job_date: rec.job_date,
                account_number: rec.account_number,
                customer_full_name: rec.customer_full_name,
                customer_mobile_number: rec.customer_mobile_number,
                customer_alt_mobile_number: rec.customer_alt_mobile_number,
                municipality: rec.municipality,
                barangay: rec.barangay,
                plan: rec.plan,
                complete_address: rec.complete_address,
                team_number: rec.team_number,
                team_leader_name: rec.team_leader_name,
                personnel_names: rec.personnel_names,
                notes: rec.notes,
              };
              try {
                await api.put(`/xlsx-imports/${id}`, payload);
              } catch (err) {
                // Ignore update errors, activation will still try
              }
            }
          });
          await Promise.all(updatePromises);

          try {
            const { data } = await api.post(`/xlsx-imports/bulk-activate`, {
              record_ids: Array.from(selected),
              team_leader_id: teamLeaderId || null,
            });
            for (const res of data) {
              if (res.status === "success") {
                activatedCount += 1;
              } else {
                failedCount += 1;
                failMessages.push(`ID ${res.id}: ${res.detail}`);
              }
            }
          } catch (err) {
            failMessages.push("Bulk activation failed: " + (err.response?.data?.detail || err.message));
          }

          setSelected(new Set());
          await refreshAll();
          setActionError(`Activated ${activatedCount}, skipped ${failedCount}.${
            failMessages.length ? " " + failMessages.join(" | ") : ""
          }`);
        };

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
              <h3 className="text-base font-semibold text-gray-800 mb-2">Activate {selected.size} Record(s)</h3>
              {allHaveTL ? (
                <>
                  <p className="text-sm text-gray-600 mb-4">
                    All selected records already have an Assigned TL. Proceed with activation?
                  </p>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setShowBulkActivate(false)}
                      className="px-4 py-2 text-sm border rounded hover:bg-gray-100"
                    >
                      Go Back
                    </button>
                    <button
                      onClick={() => doBulkActivate(null)}
                      className="px-4 py-2 text-sm rounded text-white bg-green-600 hover:bg-green-700"
                    >
                      Yes, Activate
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-4">
                    {withoutTL.length === selectedRecs.length
                      ? "Select a Team Leader for the selected records before activation."
                      : `${withoutTL.length} of ${selectedRecs.length} record(s) need a Team Leader. This will apply to records without an Assigned TL.`}
                  </p>
                  <label className="block text-sm font-medium mb-1">
                    Assigned TL <span className="text-red-500">*</span>
                  </label>
                  <div className="mb-4">
                    <SearchableTLSelect
                      options={activeTLs}
                      value={bulkActivateTLId}
                      onChange={setBulkActivateTLId}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setShowBulkActivate(false)}
                      className="px-4 py-2 text-sm border rounded hover:bg-gray-100"
                    >
                      Go Back
                    </button>
                    <button
                      disabled={!bulkActivateTLId}
                      onClick={() => doBulkActivate(bulkActivateTLId)}
                      className="px-4 py-2 text-sm rounded text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                    >
                      Yes, Activate
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
